import { fetchSiteClassification } from '../api/fetchSiteClassification';
import { nowImmediate } from '../lib/dateTime';
import {
  getCachedClassification,
  hasCachedClassificationMiss,
  setCachedClassification,
  setCachedClassificationMiss,
} from '../storage/classificationLRU';

const CLASSIFICATION_MISS_TTL_MS = 24 * 60 * 60 * 1_000;
const IPV4_OCTET_PATTERN = String.raw`(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)`;
const IPV4_HOSTNAME_PATTERN = new RegExp(
  String.raw`^${IPV4_OCTET_PATTERN}(\.${IPV4_OCTET_PATTERN}){3}$`
);

const normalizeHostname = (hostname: string): string =>
  hostname.trim().toLowerCase().replace(/\.$/, '');

export const isClassifiableHostname = (hostname: string): boolean => {
  const normalizedHostname = normalizeHostname(hostname);

  return (
    normalizedHostname !== '' &&
    normalizedHostname.includes('.') &&
    !IPV4_HOSTNAME_PATTERN.test(normalizedHostname) &&
    !normalizedHostname.includes(':')
  );
};

export const getClassification = async (hostname: string) => {
  const normalizedHostname = normalizeHostname(hostname);
  if (!isClassifiableHostname(normalizedHostname)) {
    return undefined;
  }

  const cachedClassification = getCachedClassification(normalizedHostname);
  if (cachedClassification) {
    return cachedClassification;
  }

  if (hasCachedClassificationMiss(normalizedHostname, nowImmediate())) {
    return undefined;
  }

  const apiClassification = await fetchSiteClassification(normalizedHostname);
  if (apiClassification) {
    setCachedClassification(normalizedHostname, apiClassification);
  } else if (apiClassification === undefined) {
    setCachedClassificationMiss(
      normalizedHostname,
      nowImmediate() + CLASSIFICATION_MISS_TTL_MS
    );
  }

  return apiClassification;
};
